import React from "react";
import {
  Box,
  Typography,
  Grid,
  Chip,
  useTheme,
  Theme,
  CircularProgress,
  Alert,
} from "@mui/material";
import { GateView } from "../types";

interface AggregatedGateViewProps {
  gates: GateView[];
  isLoading?: boolean;
  isError?: boolean;
  error?: string;
}

const getChipConfig = (
  color: GateView["color"]
): { label: string; color: "success" | "warning" | "error" } => {
  if (color === "red") return { label: "At Risk", color: "error" };
  if (color === "yellow") return { label: "Watch", color: "warning" };
  return { label: "On Track", color: "success" };
};

const getAccentColor = (color: GateView["color"], theme: Theme) => {
  if (color === "red") return theme.palette.error.main;
  if (color === "yellow") return theme.palette.warning.main;
  return theme.palette.success.main;
};

export const AggregatedGateView: React.FC<AggregatedGateViewProps> = ({
  gates,
  isLoading = false,
  isError = false,
  error,
}) => {
  const theme = useTheme();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error">{error || "Failed to load phase counts"}</Alert>
    );
  }

  if (gates.length === 0) {
    return null;
  }

  return (
    <Grid container spacing={2}>
      {gates.map((gate) => (
        <Grid size={{ xs: 12, sm: 6, md: 3 }} key={gate.gate}>
          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
            >
              <Typography variant="subtitle2" color="text.secondary">
                {gate.gate}
              </Typography>
            </Box>
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              gap={1}
            >
              <Typography
                variant="h4"
                fontWeight={700}
                color={getAccentColor(gate.color, theme)}
              >
                {gate.count}
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                {...getChipConfig(gate.color)}
              />
            </Box>
          </Box>
        </Grid>
      ))}
    </Grid>
  );
};
